import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Observable, throwError } from 'rxjs'
import { catchError, first, switchMap, timeout } from 'rxjs/operators'

import { VerityEnvironmentService } from '../../environments/verity-environment.service'
import { AuthService } from '../auth/auth.service'

import { isUnauthorizedError } from './http-error.utils'

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService = inject(AuthService)
  private verityEnvironmentService = inject(VerityEnvironmentService)

  private readonly publicRoutes: Array<RegExp> = [
    new RegExp('/auth/.+$'),
    new RegExp('/support/contact$'),
  ]

  private accessTokenDeferTimeout = 15000

  /**
   * Hold any requests to protected routes until fresh access token is available, failing if the token isn't
   * available before the timeout window
   */
  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!this.isProtectedRoute(request.url)) {
      return next.handle(request)
    } else {
      return this.authService.accessToken$.pipe(
        first(),
        timeout(this.accessTokenDeferTimeout),
        switchMap((token) => {
          return next
            .handle(this.addToken(request, token))
            .pipe(
              catchError((error: unknown) =>
                this.retryOn401(request, next, error),
              ),
            )
        }),
      )
    }
  }

  /**
   * On a 401 from a protected route, refresh the tokens (reusing
   * AuthService's own in-flight-request dedup, so concurrent 401s only
   * trigger a single refresh call) and retry the original request once
   * with the fresh token. Any other error, or a failed refresh, passes
   * through unchanged -- AuthService.refreshTokens() already handles a
   * revoked refresh token by logging the user out.
   */
  private retryOn401(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    error: unknown,
  ): Observable<HttpEvent<unknown>> {
    if (!isUnauthorizedError(error)) {
      return throwError(() => error)
    }

    return this.authService
      .refreshTokens()
      .pipe(
        switchMap((tokens) =>
          next.handle(this.addToken(request, tokens.accessToken)),
        ),
      )
  }

  private addToken(
    request: HttpRequest<unknown>,
    token: string,
  ): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    })
  }

  private isProtectedRoute(route: string): boolean {
    if (route.startsWith(this.verityEnvironmentService.apiUrl)) {
      return !this.publicRoutes.some((publicRoute) => publicRoute.test(route))
    }

    return false
  }
}
